import { baseApi } from '@app/store/index.api';

import {
  IWallet,
  TWalletCredentials,
  IWalletUpdateQuery,
  IAssignWalletTagsQuery,
  IWalletUpdateStatusQuery,
  IWalletUpdateStatusResponse,
  IWalletUpdateModeQuery,
  IWalletUpdateModeResponse,
  IWalletMID,
  IWalletMIDQuery,
  IWalletResponse,
  INewWalletResponse,
  INewWalletSettingsResponse,
  IEditWalletSettingsQuery,
  IWalletSettingsResponse,
  IWalletClickhouse,
  IWalletStopResponse,
  IUpdateWalletPolicyResponse,
  IUpdateWalletPolicyQuery,
  IWalletPolicyResponse,
  IAffectedByInactivationResponse,
  IWalletSectionSettingsResponse,
} from '../model/types';

const walletsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getWallets: builder.query<IWalletResponse, string>({
      query: query => {
        return {
          url: `/wallets?${query}`,
        };
      },
      providesTags: ['Wallets'],
    }),
    getWalletsClickhouse: builder.query<IWalletResponse, string>({
      query: query => {
        return {
          url: `/wallets/clickhouse?${query}`,
        };
      },
      providesTags: ['Wallets'],
    }),
    getWalletSettings: builder.query<IWalletSettingsResponse, string>({
      query: id => {
        return {
          url: `/wallets/${id}/settings`,
        };
      },
      providesTags: ['Wallets'],
    }),
    getWalletsDebug: builder.query<IWalletResponse, string>({
      query: query => {
        return {
          url: `/wallets/debug?${query}`,
        };
      },
      providesTags: ['Wallets'],
    }),
    createWallet: builder.mutation<INewWalletResponse, FormData>({
      query: formData => ({
        url: '/wallets',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Wallets'],
    }),
    createWalletSettings: builder.mutation<INewWalletSettingsResponse, IEditWalletSettingsQuery>({
      query: query => ({
        url: `/wallets/${query.id}/setup-setting`,
        method: 'POST',
        body: query,
      }),
      invalidatesTags: ['Wallets'],
    }),
    // No list invalidation: tags save via a follow-up PUT, so refetching now races that
    // write and shows stale tags. assignWalletTags refetches once the tags are in.
    updateWallet: builder.mutation<IWallet, IWalletUpdateQuery>({
      query: query => ({
        url: `/wallets/${query.id}`,
        method: 'PATCH',
        body: query,
      }),
      invalidatesTags: [],
    }),

    assignWalletTags: builder.mutation<IWallet, IAssignWalletTagsQuery>({
      query: ({ id, tag_ids }) => ({
        url: `/wallets/${id}/tags`,
        method: 'PUT',
        body: { tag_ids },
      }),
      invalidatesTags: ['Wallets'], // safe — runs after the tags are saved
    }),

    createWalletMID: builder.mutation<IWalletMID, IWalletMIDQuery>({
      query: query => ({
        url: '/wallets/child',
        method: 'POST',
        body: query,
      }),
      invalidatesTags: ['Wallets'],
    }),

    updateWalletStatus: builder.mutation<IWalletUpdateStatusResponse, IWalletUpdateStatusQuery>({
      query: query => ({
        url: `/wallets/${query.id}/status`,
        method: 'PATCH',
        body: {
          status: query.status,
        },
      }),
      invalidatesTags: ['Wallets'],
    }),

    updateWalletMode: builder.mutation<IWalletUpdateModeResponse, IWalletUpdateModeQuery>({
      query: query => ({
        url: `/wallets/${query.id}/mode`,
        method: 'PATCH',
        body: {
          mode: query.mode,
        },
      }),
      invalidatesTags: ['Wallets'],
    }),

    getWalletCredentials: builder.query<TWalletCredentials, string>({
      query: id => `/wallets/${id}/credentials`,
    }),

    stopWallet: builder.mutation<IWalletStopResponse, IWalletClickhouse>({
      query: query => ({
        url: `/wallets/${query.id}/emergency-stop`,
        method: 'POST',
      }),
      invalidatesTags: ['Wallets'],
    }),

    getWalletsPolicies: builder.query<IWalletPolicyResponse, string>({
      query: () => `/limits/presets/`,
    }),

    // Bulk wallet type update (mock endpoint for now)
    updateWalletPolicy: builder.mutation<IUpdateWalletPolicyResponse, IUpdateWalletPolicyQuery>({
      query: body => ({
        url: `/wallets/apply-policy`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Wallets'],
    }),

    affectedByAppliedPolicy: builder.query<IAffectedByInactivationResponse, { id: string }>({
      query: ({ id }) => `/wallets/${id}/limits/affected-by-inactivation`,
    }),

    getWalletSectionSettings: builder.query<IWalletSectionSettingsResponse, void>({
      query: () => '/wallets/section-settings',
    }),
  }),
});

export const {
  useGetWalletsQuery,
  useGetWalletsClickhouseQuery,
  useGetWalletSettingsQuery,
  useGetWalletsDebugQuery,
  useLazyGetWalletsQuery,
  useCreateWalletMutation,
  useCreateWalletSettingsMutation,
  useUpdateWalletMutation,
  useAssignWalletTagsMutation,
  useUpdateWalletStatusMutation,
  useUpdateWalletModeMutation,
  useLazyGetWalletCredentialsQuery,
  useCreateWalletMIDMutation,
  useStopWalletMutation,
  useUpdateWalletPolicyMutation,
  useGetWalletsPoliciesQuery,
  useLazyAffectedByAppliedPolicyQuery,
  useGetWalletSectionSettingsQuery,
} = walletsApi;
